import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Badge
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { CriteriaValidationResult } from '../../../types/dataTypes';

interface ValidationResultsTableProps {
  validationResults: CriteriaValidationResult[];
  title?: string;
  // 🔥 NEW: Props for comparison
  previousResults?: CriteriaValidationResult[];
  showComparison?: boolean;
  iterationNumber?: number;
}

const ValidationResultsTable: React.FC<ValidationResultsTableProps> = ({ 
  validationResults, 
  title = "CV Validation Results",
  previousResults,
  showComparison = false,
  iterationNumber = 1
}) => {
  if (validationResults.length === 0) return null;

  const overallScore = (validationResults.filter(result => result.requirementMet).length 
  / validationResults.length) * 100;

  // 🔥 NEW: Calculate previous score for comparison
  const previousScore = previousResults ? 
    (previousResults.filter(result => result.requirementMet).length / previousResults.length) * 100 : 0;

  // 🔥 NEW: Get improvement status for each result
  const getResultComparison = (currentResult: CriteriaValidationResult) => {
    if (!previousResults || !showComparison) return null;
    
    const previousResult = previousResults.find(pr => pr.key === currentResult.key);
    if (!previousResult) return null;

    const scoreImprovement = currentResult.scorePercent - previousResult.scorePercent;
    const statusChanged = currentResult.requirementMet !== previousResult.requirementMet;

    return {
      scoreImprovement,
      statusChanged,
      wasImproved: scoreImprovement > 0 || (statusChanged && currentResult.requirementMet),
      wasWorsened: scoreImprovement < 0 || (statusChanged && !currentResult.requirementMet)
    };
  };

  const getCategoryColor = (key: string) => {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('education')) return '#1976d2';
    if (lowerKey.includes('experience')) return '#388e3c';
    if (lowerKey.includes('language')) return '#f57c00';
    if (lowerKey.includes('location') || lowerKey.includes('country') || lowerKey.includes('geographic')) return '#7b1fa2';
    if (lowerKey.includes('skill') || lowerKey.includes('programming')) return '#d32f2f';
    if (lowerKey.includes('security') || lowerKey.includes('clearance')) return '#e91e63';
    if (lowerKey.includes('project') || lowerKey.includes('management')) return '#ff9800';
    return '#616161';
  };

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      {/* Header with Overall Score */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            color: '#4CAF50', 
            fontWeight: 'bold'
          }}
        >
          {title} {iterationNumber > 1 && `(v${iterationNumber})`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Overall Score: {overallScore.toFixed(1)}%
          </Typography>
          {/* 🔥 NEW: Show score comparison */}
          {showComparison && previousResults && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {overallScore > previousScore ? (
                <TrendingUpIcon sx={{ color: '#4CAF50' }} />
              ) : overallScore < previousScore ? (
                <TrendingDownIcon sx={{ color: '#f44336' }} />
              ) : null}
              <Typography variant="body2" sx={{ 
                color: overallScore > previousScore ? '#4CAF50' : overallScore < previousScore ? '#f44336' : '#666',
                fontWeight: 'bold'
              }}>
                {overallScore > previousScore ? '+' : ''}{(overallScore - previousScore).toFixed(1)}%
              </Typography>
            </Box>
          )}
          <LinearProgress 
            variant="determinate" 
            value={overallScore} 
            sx={{ 
              width: 100, 
              height: 8, 
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: overallScore >= 70 ? '#4CAF50' : overallScore >= 50 ? '#FF9800' : '#f44336'
              }
            }}
          />
        </Box>
      </Box>

      <TableContainer 
        component={Paper} 
        elevation={3}
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #e0e0e0'
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#333', borderBottom: '2px solid #4CAF50', width: '200px' }}>
                Criterion
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#333', borderBottom: '2px solid #4CAF50', width: '100px' }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#333', borderBottom: '2px solid #4CAF50', width: '120px' }}>
                Score
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#333', borderBottom: '2px solid #4CAF50' }}>
                Comments & Suggestions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validationResults.map((result, index) => {
              // 🔥 NEW: Get comparison data
              const comparison = getResultComparison(result);
              const isHighlighted = comparison && (comparison.wasImproved || comparison.wasWorsened);
              const isGreyedOut = showComparison && previousResults && !isHighlighted;

              return (
                <TableRow 
                  key={index}
                  sx={{ 
                    '&:nth-of-type(odd)': { 
                      backgroundColor: isGreyedOut ? '#f9f9f9' : '#fafafa' 
                    },
                    '&:hover': { 
                      backgroundColor: result.requirementMet ? '#e8f5e8' : '#ffeaea' 
                    },
                    transition: 'background-color 0.2s ease',
                    // 🔥 NEW: Highlight improved/worsened results
                    ...(isHighlighted && {
                      backgroundColor: comparison?.wasImproved ? '#e8f5e8' : '#ffeaea',
                      border: `2px solid ${comparison?.wasImproved ? '#4CAF50' : '#f44336'}`,
                    }),
                    // 🔥 NEW: Grey out unchanged results
                    ...(isGreyedOut && {
                      opacity: 0.6
                    })
                  }}
                >
                  {/* Criterion Column */}
                  <TableCell sx={{ borderBottom: '1px solid #e0e0e0', verticalAlign: 'top', py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Badge
                        badgeContent={
                          comparison?.wasImproved ? <TrendingUpIcon sx={{ fontSize: 12, color: '#4CAF50' }} /> :
                          comparison?.wasWorsened ? <TrendingDownIcon sx={{ fontSize: 12, color: '#f44336' }} /> : 0
                        }
                        invisible={!isHighlighted}
                      >
                        <Avatar 
                          sx={{ 
                            width: 36, 
                            height: 36, 
                            backgroundColor: getCategoryColor(result.key),
                            color: 'white',
                            fontSize: '1.2rem'
                          }}
                        >
                          {result.icon || '📝'}
                        </Avatar>
                      </Badge>
                      <Box>
                        <Chip
                          label={result.key}
                          variant="outlined"
                          size="small"
                          sx={{
                            fontWeight: 'bold',
                            borderColor: getCategoryColor(result.key),
                            color: getCategoryColor(result.key),
                            maxWidth: '140px'
                          }}
                        />
                        <Typography variant="body2" sx={{ mt: 0.5, color: '#666', fontSize: '0.85rem' }}>
                          {result.value}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Status Column */}
                  <TableCell sx={{ borderBottom: '1px solid #e0e0e0', verticalAlign: 'top', py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {result.requirementMet ? (
                        <>
                          <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 24 }} />
                          <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                            Met
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CancelIcon sx={{ color: '#f44336', fontSize: 24 }} />
                          <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 'bold' }}>
                            Not Met
                          </Typography>
                        </>
                      )}
                      {/* 🔥 NEW: Show status change indicator */}
                      {comparison?.statusChanged && (
                        <Chip
                          label={comparison.wasImproved ? "NEW!" : "LOST!"}
                          size="small"
                          color={comparison.wasImproved ? "success" : "error"}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                  </TableCell>

                  {/* Score Column */}
                  <TableCell sx={{ borderBottom: '1px solid #e0e0e0', verticalAlign: 'top', py: 2 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {result.scorePercent.toFixed(1)}%
                        </Typography>
                        {/* 🔥 NEW: Show score improvement */}
                        {comparison && comparison.scoreImprovement !== 0 && (
                          <Typography variant="caption" sx={{ 
                            color: comparison.scoreImprovement > 0 ? '#4CAF50' : '#f44336',
                            fontWeight: 'bold'
                          }}>
                            ({comparison.scoreImprovement > 0 ? '+' : ''}{comparison.scoreImprovement.toFixed(1)}%)
                          </Typography>
                        )}
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={result.scorePercent} 
                        sx={{ 
                          height: 6, 
                          borderRadius: 3,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: result.requirementMet ? '#4CAF50' : '#f44336'
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                        Weight: {result.overallWeightPercent}%
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Comments & Suggestions Column */}
                  <TableCell sx={{ borderBottom: '1px solid #e0e0e0', verticalAlign: 'top', py: 2 }}>
                    <Box>
                      {result.comments.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
                            Comments:
                          </Typography>
                          <List dense sx={{ py: 0 }}>
                            {result.comments.map((comment, idx) => (
                              <ListItem key={idx} sx={{ py: 0, px: 0 }}>
                                <ListItemText 
                                  primary={comment} 
                                  sx={{ 
                                    '& .MuiListItemText-primary': { 
                                      fontSize: '0.875rem', 
                                      color: '#555' 
                                    } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      {result.suggestions.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                            Suggestions:
                          </Typography>
                          <List dense sx={{ py: 0 }}>
                            {result.suggestions.map((suggestion, idx) => (
                              <ListItem key={idx} sx={{ py: 0, px: 0 }}>
                                <ListItemText 
                                  primary={suggestion} 
                                  sx={{ 
                                    '& .MuiListItemText-primary': { 
                                      fontSize: '0.875rem', 
                                      color: '#1976d2' 
                                    } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer Summary */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {validationResults.filter(r => r.requirementMet).length} of {validationResults.length} requirements met
          {showComparison && previousResults && (
            ` (${validationResults.filter(r => r.requirementMet).length - previousResults.filter(r => r.requirementMet).length >= 0 ? '+' : ''}${validationResults.filter(r => r.requirementMet).length - previousResults.filter(r => r.requirementMet).length} from previous version)`
          )}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip 
            label={`${validationResults.filter(r => r.requirementMet).length} Met`} 
            color="success" 
            size="small" 
          />
          <Chip 
            label={`${validationResults.filter(r => !r.requirementMet).length} Not Met`} 
            color="error" 
            size="small" 
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ValidationResultsTable;